import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { MenuItemDetails as View } from '@views';
import { routes } from '@constants';
import {
  getMenuCategories,
  getMenuSubcategories,
  getMenuOptions,
  getMenuSizes,
  getItemPrices,
  getOptionsWithValuesSelected,
  incrementOptionValueQuantity,
  decrementOptionValueQuantity,
} from '@helpers/menuItemDetails';

const MenuItemDetails = () => {
  const {
    categories, subcategories, options, optionValues, items, sizes,
  } = useSelector(state => state.menu);

  const history = useHistory();
  const location = useLocation();
  const { itemId } = useParams();

  const { selectedCategoryId } = location.state;

  const [isDrawerOpened, setDrawerOpen] = useState(true);
  const [sizeSelected, setSizeSelected] = useState(
    items[itemId].sizeIds.find(sizeId => sizes[sizeId].specialPrice)
  || items[itemId].sizeIds.find(sizeId => sizes[sizeId].preSelected)
  || items[itemId].sizeIds[0],
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [optionsList, setOptionsList] = useState([]);

  const [itemPrice, itemSpecialPrice] = getItemPrices(sizes, sizeSelected, optionsList);

  useEffect(() => {
    if (!location.state) {
      history.goBack();
    }
  }, []);

  useEffect(() => {
    const selectedSizeOptions = getMenuOptions(options, sizes, sizeSelected, optionValues);
    setOptionsList(selectedSizeOptions);
  }, [sizeSelected]);

  const handleMenuItemProcess = id => () => {
    history.push({
      pathname: routes.itemEdit,
      state: {
        id,
        categoryId: selectedCategoryId,
      },
    });
  };

  const handleSizeSelect = sizeId => () => {
    setSizeSelected(sizeId);
  };

  const handleOptionValueSelect = (optionId, valueId) => () => {
    setOptionsList(prevOptions => getOptionsWithValuesSelected(prevOptions, optionId, valueId));
  };

  const handleModalChangeState = () => {
    setIsModalOpen(modalState => !modalState);
  };

  const handleCategorySelect = categoryId => () => {
    history.push({
      pathname: routes.menuItems,
      state: {
        selectedCategoryId: categoryId,
      },
    });
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(prevIsDrawerOpened => !prevIsDrawerOpened);
  };

  const handleBackUp = () => {
    history.push({
      pathname: routes.menuItems,
      state: {
        selectedCategoryId,
      },
    });
  };

  const handleOptionQuantityDecrement = (valueId, optionId) => () => {
    setOptionsList(prevOptions => decrementOptionValueQuantity(prevOptions, optionId, valueId));
  };

  const handleOptionQuantityIncrement = (valueId, optionId) => () => {
    setOptionsList(prevOptions => incrementOptionValueQuantity(prevOptions, optionId, valueId));
  };

  const memoizedCategories = useMemo(() => (
    getMenuCategories(categories)
  ), [selectedCategoryId]);

  const memoizedSubcategories = useMemo(() => (
    getMenuSubcategories(subcategories, categories, selectedCategoryId)
  ), [selectedCategoryId]);

  const memoizedSize = useMemo(() => (
    getMenuSizes(items, itemId, sizes, sizeSelected)
  ), [sizeSelected]);

  return (
    <View
      specialPrice={itemSpecialPrice}
      onOptionQuantityDecrement={handleOptionQuantityDecrement}
      onOptionQuantityIncrement={handleOptionQuantityIncrement}
      itemId={itemId}
      onMenuItemProcess={handleMenuItemProcess}
      categories={memoizedCategories}
      subcategories={memoizedSubcategories}
      selectedCategoryId={selectedCategoryId}
      isModalOpen={isModalOpen}
      size={memoizedSize}
      onModalChangeState={handleModalChangeState}
      onSizeSelect={handleSizeSelect}
      onOptionValueSelect={handleOptionValueSelect}
      onCategorySelect={handleCategorySelect}
      title={items[itemId].name}
      imageSrc={items[itemId].image[0]?.src}
      price={itemPrice}
      description={items[itemId].description}
      isDrawerOpened={isDrawerOpened}
      optionsList={optionsList}
      onDrawerOpen={handleDrawerOpen}
      onBackUp={handleBackUp}
    />
  );
};

export default MenuItemDetails;
